import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useApp } from "../lib/AppContext";
import { useFetch } from "../lib/useFetch";
import { t } from "../lib/i18n";
import { fmt, rm, pnlClass, ts } from "../lib/api";
import { Panel, Stat, Tag, PageHead, Empty, Spinner } from "../components/common";
import { AccountsOverview } from "../components/AccountMode";
import LiveHome from "./LiveHome";

export const EquityChart = ({ data, h = 180 }) => (
  <ResponsiveContainer width="100%" height={h}>
    <AreaChart data={data}>
      <defs><linearGradient id="eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
      <XAxis dataKey="x" hide />
      <YAxis domain={["auto", "auto"]} width={60} tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
      <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 11 }} />
      <Area type="monotone" dataKey="y" stroke="#3B82F6" fill="url(#eq)" strokeWidth={1.5} />
    </AreaChart>
  </ResponsiveContainer>
);

export default function Home() {
  const { overview, lang, view, setView } = useApp();
  const { data: scan } = useFetch("/hunter/scan", 120000);
  const { data: pf } = useFetch("/portfolio", 60000);
  const wantsBuy = new URLSearchParams(window.location.search).get("buy");
  useEffect(() => { if (wantsBuy && view !== "live") setView("live"); }, [wantsBuy, view, setView]);
  if (view === "live") return <LiveHome />;
  if (!overview) return <Spinner />;
  const s = overview.state;
  const lc = overview.last_cycle;
  return (
    <div data-testid="home-page">
      <PageHead title={lang === "ms" ? "Pusat Kawalan" : "Command Center"} sub={`Base currency MYR · Server ${ts(overview.server_time)} · Broker: Moomoo only · Bursa/HK prices delayed feed`} />
      <AccountsOverview />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat testid="stat-equity" label={t(lang, "equity")} value={rm(s.equity)} sub={`${s.total_return_pct}% since start`} />
        <Stat testid="stat-daily-pnl" label={t(lang, "daily_pnl")} value={rm(s.daily_pnl)} tone={pnlClass(s.daily_pnl)} sub={`target ${rm(s.daily_target)} · ${s.target_progress_pct}%`} />
        <Stat testid="stat-weekly-pnl" label={t(lang, "weekly_pnl")} value={rm(s.weekly_pnl)} tone={pnlClass(s.weekly_pnl)} />
        <Stat testid="stat-open-positions" label={t(lang, "open_positions")} value={`${s.open_count} / ${overview.settings.max_positions}`} sub={`${s.pending_count} pending`} />
        <Stat testid="stat-risk-budget" label="Remaining daily risk" value={rm(s.remaining_daily_risk)} sub={`cash ${rm(s.cash)}`} />
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <Panel title="AI manager · last decision" className="lg:col-span-1" testid="last-decision-panel">
          {lc ? (
            <>
              <div className="flex items-center gap-2"><span data-testid="last-decision-outcome" className={`font-display text-2xl font-extrabold ${lc.outcome === "TRADE" ? "text-emerald-600" : "text-slate-700"}`}>{lc.outcome === "TRADE" ? "TRADE" : t(lang, "no_trade")}</span></div>
              <div className="mono mt-1 text-[10px] text-slate-500">{ts(lc.finished_at)} · {lc.trigger}</div>
              <ul className="mt-3 space-y-1">{lc.decisions.map((d, i) => <li key={i} className="text-xs text-slate-600">— {d}</li>)}</ul>
            </>
          ) : <Empty>No cycle yet</Empty>}
          <div className="mt-4 flex flex-wrap gap-2">
            {overview.sessions.map((x) => <span key={x.exchange} className={`mono rounded-sm border px-2 py-0.5 text-[10px] ${x.open ? "border-emerald-500/40 text-emerald-600" : "border-[#E2E8F0] text-slate-500"}`}>{x.exchange} {x.status.toUpperCase()}</span>)}
          </div>
        </Panel>
        <Panel title="Equity (MYR)" className="lg:col-span-2" testid="equity-panel">
          {pf?.snapshots?.length > 1 ? <EquityChart data={pf.snapshots.map((x) => ({ x: ts(x.ts), y: x.equity }))} /> : <Empty>Equity curve builds as the AI manager runs cycles.</Empty>}
        </Panel>
      </div>
      <Panel title="Top ranked opportunities" className="mt-3" right={<Link to="/hunter" className="text-[11px] text-blue-600 hover:underline" data-testid="home-view-hunter">Open hunter →</Link>} testid="home-opportunities">
        {!scan ? <Spinner /> : (
          <div className="custom-scrollbar overflow-x-auto">
            <table className="tbl w-full">
              <thead><tr><th>Symbol</th><th>Exch</th><th>Action</th><th>Status</th><th>Score</th><th>Regime</th><th>Entry</th><th>SL</th><th>TP1</th><th>RR</th><th>Why / why not</th></tr></thead>
              <tbody>{scan.rows.slice(0, 6).map((r) => (
                <tr key={r.symbol}>
                  <td className="mono text-slate-900">{r.symbol}</td><td className="text-slate-600">{r.exchange}</td><td>{r.action ? <Tag v={r.action} testid={`home-action-${r.symbol}`} /> : "—"}</td><td><Tag v={r.status} /></td>
                  <td className="mono">{fmt(r.score, 0)}</td><td className="text-slate-600">{r.regime}</td>
                  <td className="mono">{r.entry}</td><td className="mono text-red-600">{r.stop}</td><td className="mono text-emerald-600">{r.tp1}</td><td className="mono">{r.rr}</td>
                  <td className="max-w-[340px] truncate text-slate-600">{(r.status === "QUALIFIED" ? r.reasons : r.rejects)?.[0]}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
